import {
  EMERGENCY_EQUIPMENT_CONTACT_STATES,
  StateAbbreviation,
} from "encamp-shared/src/constants/states";
import { contactReportRoleToLabel } from "encamp-shared/src/utils/contactReportRole";
import { ContactReportRole, TankOwnerType } from "generated-graphql/graphql";

export const getEPCRAContactRoles = (
  ...facilityStates: Array<string | null | undefined>
): ContactReportRole[] => {
  const epcraRoles = [
    ContactReportRole.EpcraTierIiContact,
    ContactReportRole.EpcraEmergencyContact,
    ContactReportRole.EpcraEmergencyCoordinator,
    ContactReportRole.EpcraOwnerOperator,
    ContactReportRole.EpcraBilling,
    ContactReportRole.EpcraChemicalCarrier,
    ContactReportRole.EpcraT2MDirectSiteCommunication,
    ContactReportRole.EpcraT2MParentCompany,
    ContactReportRole.EpcraRegionalEnvironmentalContact,
  ];

  if (
    facilityStates.length &&
    facilityStates.every((state) =>
      EMERGENCY_EQUIPMENT_CONTACT_STATES.includes(state as StateAbbreviation)
    )
  ) {
    epcraRoles.push(ContactReportRole.EpcraEmergencyEquipmentContact);
  }

  if (
    facilityStates.length &&
    facilityStates.every((state) => state === "CA")
  ) {
    const caRoles = [
      ContactReportRole.EpcraCaOperator,
      ContactReportRole.EpcraCaOwner,
      ContactReportRole.EpcraCaPropertyOwner,
      ContactReportRole.EpcraCaTankOperator,
      ContactReportRole.EpcraCaTankOwner,
    ];
    return epcraRoles.concat(caRoles);
  }

  return epcraRoles;
};

// You can find these documented here: https://encamp.notion.site/RCRA-Roles-2572f9edc08f8062ae74d3881923d081
export const getRcraContactRoles = (
  ...facilityStates: Array<string | null | undefined>
): ContactReportRole[] => {
  const rcraRoles = [
    ContactReportRole.RcraOwner,
    ContactReportRole.RcraOperator,
    ContactReportRole.RcraSiteContact,
    ContactReportRole.RcraAuthorizedRepresentative,
    ContactReportRole.RcraSiteManager,
  ];

  if (
    [
      "AL",
      "AR",
      "AZ",
      "CA",
      "CO",
      "DE",
      "IA",
      "IL",
      "IN",
      "KS",
      "KY",
      "LA",
      "MI",
      "MN",
      "MO",
      "MS",
      "MT",
      "NJ",
      "NM",
      "NY",
      "OK",
      "OR",
      "RI",
      "SC",
      "VT",
      "WA",
      "WI",
      "WV",
    ].some((state) => facilityStates.includes(state))
  ) {
    rcraRoles.push(ContactReportRole.RcraBillingContact);
  }

  if (["KY", "LA"].some((state) => facilityStates.includes(state))) {
    rcraRoles.push(ContactReportRole.RcraRealPropertyOwner);
  }

  if (facilityStates.includes("FL")) {
    rcraRoles.push(ContactReportRole.RcraHurricanePointOfContact);
  }

  return rcraRoles;
};

/**
 * Maps ContactReportRole to human-readable label.
 * Uses shared utility from encamp-shared for consistency across the codebase.
 */
export const contactRoleToLabel = (role: ContactReportRole): string => {
  return contactReportRoleToLabel(role);
};

export const getTankOwnerTypeOptions = () => {
  return [
    { display: "None", value: null },
    {
      display: "Local Agency/District",
      value: TankOwnerType.LocalAgency,
    },
    {
      display: "County Agency",
      value: TankOwnerType.CountyAgency,
    },
    {
      display: "State Agency",
      value: TankOwnerType.StateAgency,
    },
    {
      display: "Federal Agency",
      value: TankOwnerType.FederalAgency,
    },
    {
      display: "Non-Government",
      value: TankOwnerType.NonGovernment,
    },
  ];
};
